// src/features/insightsSlice.ts
import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import { insightsApi } from '~/api/insights-api'; // The RTK Query API service

// Since the API response type is `any`, we'll define our state shape
// with `any` as well. Using more specific types is recommended if you know the data structure.
interface InsightsState {
  overview: any | null;
  conversations: any | null;
  ratings: any | null;
  resolutionRate: any | null;
  knowledgeGaps: any | null;
  actions: any | null;

  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: InsightsState = {
  overview: null,
  conversations: null,
  ratings: null,
  resolutionRate: null,
  knowledgeGaps: null,
  actions: null,
  status: 'idle',
  error: null,
};

const insightsSlice = createSlice({
  name: 'insights',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Each matcher now takes the data from `action.payload.response`
    // and puts it into the corresponding part of the state.
    builder
      .addMatcher(insightsApi.endpoints.getInsights.matchFulfilled, (state, action) => {
        state.overview = action.payload.response;
      })
      .addMatcher(insightsApi.endpoints.getInsightsConversations.matchFulfilled, (state, action) => {
        state.conversations = action.payload.response;
      })
      .addMatcher(insightsApi.endpoints.getInsightsRatings.matchFulfilled, (state, action) => {
        state.ratings = action.payload.response;
      })
      .addMatcher(insightsApi.endpoints.getInsightsResolutionRate.matchFulfilled, (state, action) => {
        state.resolutionRate = action.payload.response;
      })
      .addMatcher(insightsApi.endpoints.getInsightsKnowledgeGaps.matchFulfilled, (state, action) => {
        state.knowledgeGaps = action.payload.response;
      })
      .addMatcher(insightsApi.endpoints.getInsightsActions.matchFulfilled, (state, action) => {
        state.actions = action.payload.response;
      })
      // Use `isAnyOf` to handle loading/success/error for all endpoints together.
      .addMatcher(isAnyOf(insightsApi.endpoints.getInsights.matchPending, insightsApi.endpoints.getInsightsConversations.matchPending, insightsApi.endpoints.getInsightsRatings.matchPending, insightsApi.endpoints.getInsightsResolutionRate.matchPending, insightsApi.endpoints.getInsightsKnowledgeGaps.matchPending, insightsApi.endpoints.getInsightsActions.matchPending), (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addMatcher(isAnyOf(insightsApi.endpoints.getInsights.matchFulfilled, insightsApi.endpoints.getInsightsConversations.matchFulfilled, insightsApi.endpoints.getInsightsRatings.matchFulfilled, insightsApi.endpoints.getInsightsResolutionRate.matchFulfilled, insightsApi.endpoints.getInsightsKnowledgeGaps.matchFulfilled, insightsApi.endpoints.getInsightsActions.matchFulfilled), (state) => {
        state.status = 'succeeded';
      })
      .addMatcher(isAnyOf(insightsApi.endpoints.getInsights.matchRejected, insightsApi.endpoints.getInsightsConversations.matchRejected, insightsApi.endpoints.getInsightsRatings.matchRejected, insightsApi.endpoints.getInsightsResolutionRate.matchRejected, insightsApi.endpoints.getInsightsKnowledgeGaps.matchRejected, insightsApi.endpoints.getInsightsActions.matchRejected), (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to fetch insights data';
      });
  },
});

export default insightsSlice.reducer;
